'use client';

import { useState, useEffect } from 'react';
import { 
  Rocket, 
  Plus, 
  Edit2, 
  Trash2, 
  CheckCircle, 
  Clock, 
  Archive,
  Save,
  Loader2,
  Link2,
  Calendar,
  X,
  Search,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import {
  useReleases,
  useCreateRelease,
  useUpdateRelease,
  useUpdateReleaseStatus,
  useLinkEvidence,
  useDeleteRelease,
  type Release,
} from '@/hooks/use-releases';
import { useEvidence } from '@/hooks/use-evidence';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ReleasePanelProps {
  projectId: string;
  projectName?: string;
}

const STATUS_CONFIG = {
  DRAFT: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: Rocket },
  READY: { label: 'Ready', color: 'bg-blue-100 text-blue-800', icon: Clock },
  APPROVED: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  RELEASED: { label: 'Released', color: 'bg-purple-100 text-purple-800', icon: Rocket },
  ROLLED_BACK: { label: 'Rolled Back', color: 'bg-red-100 text-red-800', icon: Archive },
};

const VALID_TRANSITIONS: Record<string, string[]> = {
  DRAFT: ['READY'],
  READY: ['APPROVED', 'DRAFT'],
  APPROVED: ['RELEASED', 'DRAFT'],
  RELEASED: ['ROLLED_BACK'],
  ROLLED_BACK: ['DRAFT'],
};

export function ReleasePanel({ projectId, projectName = 'Project' }: ReleasePanelProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRelease, setEditingRelease] = useState<Release | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<Release['status'] | undefined>();
  
  const { toast } = useToast();
  const { data: releases, isLoading } = useReleases(projectId, { status: statusFilter });
  const createMutation = useCreateRelease(projectId);
  const updateMutation = useUpdateRelease(projectId);
  const statusMutation = useUpdateReleaseStatus(projectId);
  const deleteMutation = useDeleteRelease(projectId);

  const handleCreate = async (data: Partial<Release>) => {
    try {
      await createMutation.mutateAsync(data);
      toast({ title: 'Release created', description: 'Release has been created successfully.' });
      setShowCreateDialog(false);
    } catch (error) {
      toast({ 
        title: 'Creation failed', 
        description: error instanceof Error ? error.message : 'Failed to create release',
        variant: 'destructive',
      });
    }
  };

  const handleUpdate = async (releaseId: string, data: Partial<Release>) => {
    try {
      await updateMutation.mutateAsync({ releaseId, data });
      toast({ title: 'Release updated', description: 'Release has been updated successfully.' });
      setEditingRelease(null);
    } catch (error) {
      toast({ 
        title: 'Update failed', 
        description: error instanceof Error ? error.message : 'Failed to update release',
        variant: 'destructive',
      });
    }
  };

  const handleStatusChange = async (releaseId: string, newStatus: Release['status']) => {
    try {
      await statusMutation.mutateAsync({ releaseId, status: newStatus });
      toast({ title: 'Status updated', description: `Release status changed to ${newStatus}.` });
    } catch (error) {
      toast({ 
        title: 'Status update failed', 
        description: error instanceof Error ? error.message : 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (releaseId: string) => {
    try {
      await deleteMutation.mutateAsync(releaseId);
      toast({ title: 'Release deleted', description: 'Release has been deleted successfully.' });
      setShowDeleteConfirm(null);
    } catch (error) {
      toast({ 
        title: 'Deletion failed', 
        description: error instanceof Error ? error.message : 'Failed to delete release',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="border-b bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Releases</h1>
            <p className="text-sm text-gray-600 mt-1">Manage releases for {projectName}</p>
          </div>
          <div className="flex items-center gap-3">
            <Select 
              value={statusFilter || 'all'} 
              onValueChange={(v) => setStatusFilter(v === 'all' ? undefined : v as Release['status'])}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                  <SelectItem key={value} value={value}>
                    {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Release
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {!releases || releases.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Rocket className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Releases</h3>
            <p className="text-gray-600 mb-6">Create your first release to get started.</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Release
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {releases.map((release) => {
              const statusConfig = STATUS_CONFIG[release.status];
              const StatusIcon = statusConfig.icon;
              const availableTransitions = VALID_TRANSITIONS[release.status] || [];
              
              return (
                <Card key={release.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <CardTitle className="text-lg">{release.title}</CardTitle>
                          <Badge className={cn(statusConfig.color, 'flex items-center gap-1')}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </Badge>
                        </div>
                        <CardDescription>Version {release.version}</CardDescription>
                        {release.description && (
                          <p className="text-sm text-gray-600 mt-2">{release.description}</p>
                        )}
                        {release.plannedDate && (
                          <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span>Planned: {new Date(release.plannedDate).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingRelease(release)}
                        >
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowDeleteConfirm(release.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {release.evidences && release.evidences.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Linked Evidence ({release.evidences.length})</h4>
                          <div className="flex flex-wrap gap-2">
                            {release.evidences.map((evidence) => (
                              <Badge key={evidence.id} variant="outline">
                                <Link2 className="w-3 h-3 mr-1" />
                                {evidence.title}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {availableTransitions.length > 0 && (
                        <div className="flex items-center gap-2 pt-2 border-t">
                          <span className="text-sm text-gray-600">Change status:</span>
                          {availableTransitions.map((transition) => (
                            <Button
                              key={transition}
                              variant="outline"
                              size="sm"
                              onClick={() => handleStatusChange(release.id, transition as Release['status'])}
                              disabled={statusMutation.isPending}
                            >
                              {STATUS_CONFIG[transition as keyof typeof STATUS_CONFIG].label}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <ReleaseDialog
        isOpen={showCreateDialog || editingRelease !== null}
        onClose={() => {
          setShowCreateDialog(false);
          setEditingRelease(null);
        }}
        onSave={editingRelease 
          ? (data) => handleUpdate(editingRelease.id, data)
          : handleCreate
        }
        release={editingRelease}
        isLoading={createMutation.isPending || updateMutation.isPending}
        projectId={projectId}
      />

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Release</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this release? This action cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDelete(showDeleteConfirm)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

interface ReleaseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<Release>) => Promise<void>;
  release?: Release | null;
  isLoading?: boolean;
  projectId: string;
}

function ReleaseDialog({ isOpen, onClose, onSave, release, isLoading, projectId }: ReleaseDialogProps) {
  const { toast } = useToast();
  const { data: evidence } = useEvidence(projectId);
  const linkEvidenceMutation = useLinkEvidence(projectId);
  
  const [formData, setFormData] = useState<Partial<Release>>({
    version: release?.version || '',
    title: release?.title || '',
    description: release?.description || '',
    releaseNotes: release?.releaseNotes || '',
    plannedDate: release?.plannedDate || undefined,
  });

  // Evidence linking state
  const [selectedEvidenceIds, setSelectedEvidenceIds] = useState<string[]>(() => 
    release?.evidences?.map(ev => ev.id) || []
  );
  const [evidenceSearch, setEvidenceSearch] = useState('');

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        version: release?.version || '',
        title: release?.title || '',
        description: release?.description || '',
        releaseNotes: release?.releaseNotes || '',
        plannedDate: release?.plannedDate || undefined,
      });
      setSelectedEvidenceIds(release?.evidences?.map(ev => ev.id) || []);
      setEvidenceSearch('');
    }
  }, [isOpen, release]);

  // Filter evidence by search
  const filteredEvidence = evidence?.filter(ev => 
    ev.title.toLowerCase().includes(evidenceSearch.toLowerCase()) ||
    ev.type.toLowerCase().includes(evidenceSearch.toLowerCase()) ||
    ev.description?.toLowerCase().includes(evidenceSearch.toLowerCase())
  ) || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
    
    // Handle evidence linking after save (if release exists)
    if (release?.id) {
      const currentLinkedIds = release.evidences?.map(ev => ev.id) || [];
      const toLink = selectedEvidenceIds.filter(id => !currentLinkedIds.includes(id));
      const toUnlink = currentLinkedIds.filter(id => !selectedEvidenceIds.includes(id));
      
      // Link new evidence
      for (const evidenceId of toLink) {
        try {
          await linkEvidenceMutation.mutateAsync({ releaseId: release.id, evidenceId });
        } catch (error) {
          toast({
            title: 'Failed to link evidence',
            description: error instanceof Error ? error.message : 'Unknown error',
            variant: 'destructive',
          });
        }
      }
      
      // Note: Unlinking would require a DELETE endpoint - for now, we'll just link new ones
      // The backend should handle this via the link endpoint (toggle behavior)
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{release ? 'Edit Release' : 'Create Release'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Version *</label>
            <Input
              value={formData.version}
              onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              required
              className="mt-1"
              placeholder="e.g., 1.0.0"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Description</label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Release Notes</label>
            <Textarea
              value={formData.releaseNotes || ''}
              onChange={(e) => setFormData({ ...formData, releaseNotes: e.target.value })}
              rows={5}
              className="mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Planned Date</label>
            <Input
              type="datetime-local"
              value={formData.plannedDate ? new Date(formData.plannedDate).toISOString().slice(0, 16) : ''}
              onChange={(e) => setFormData({ ...formData, plannedDate: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
              className="mt-1"
            />
          </div>

          {/* Evidence Linking */}
          {release && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Linked Evidence</label>
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search evidence..."
                    value={evidenceSearch}
                    onChange={(e) => setEvidenceSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="border rounded-lg p-2 max-h-40 overflow-y-auto">
                  {filteredEvidence.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-2">No evidence found</p>
                  ) : (
                    <div className="space-y-1">
                      {filteredEvidence.map((ev) => (
                        <label
                          key={ev.id}
                          className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedEvidenceIds.includes(ev.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedEvidenceIds([...selectedEvidenceIds, ev.id]);
                              } else {
                                setSelectedEvidenceIds(selectedEvidenceIds.filter(id => id !== ev.id));
                              }
                            }}
                            className="rounded"
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium">{ev.title}</div>
                            <div className="text-xs text-gray-500 flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {ev.type.replace('_', ' ')}
                              </Badge>
                              {ev.description && (
                                <span className="truncate">{ev.description}</span>
                              )}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {selectedEvidenceIds.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {selectedEvidenceIds.map((id) => {
                      const ev = evidence?.find(e => e.id === id);
                      return ev ? (
                        <Badge key={id} variant="outline" className="flex items-center gap-1">
                          <Link2 className="w-3 h-3" />
                          {ev.title}
                          <button
                            type="button"
                            onClick={() => setSelectedEvidenceIds(selectedEvidenceIds.filter(i => i !== id))}
                            className="ml-1 hover:text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
